import { useEffect, useRef, useState } from "react";
import { Camera, FileVideo, Trash2, Upload } from "lucide-react";
import clsx from "clsx";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as mediaApi from "../../api/media";
import { MediaAllegato, MediaEntityType } from "../../api/media";
import { Button } from "./Button";
import { AuthedImage } from "./AuthedImage";
import { MediaLightbox } from "./MediaLightbox";
import { ACCEPT_FOTO, ACCEPT_FOTO_VIDEO } from "../../utils/tipiFileCaricati";

// ============================================================================
// MediaThumb — singola miniatura (foto o video) + eliminazione, condivisa tra
// MediaUploader (griglia generica) e CategorizedMediaUploader (per categoria)
// ============================================================================
function MediaThumb({
  item,
  isDeleting,
  canEdit,
  onDelete,
  showCategoryBadge = false,
  onOpen,
}: {
  item: MediaAllegato;
  isDeleting: boolean;
  canEdit: boolean;
  onDelete: () => void;
  showCategoryBadge?: boolean;
  /** solo per le foto: apre la lightbox. Assente sui video, che restano fermi. */
  onOpen?: () => void;
}) {
  return (
    <div
      className={clsx(
        "group relative aspect-square overflow-hidden rounded-lg border border-line bg-black/5",
        isDeleting && "opacity-50"
      )}
    >
      {item.tipo === "foto" ? (
        // Bottone e non un <img> nudo: la foto si apre ingrandita e deve essere
        // raggiungibile anche da tastiera. Resta FRATELLO del cestino qui sotto
        // invece di contenerlo — un bottone dentro un bottone non e' HTML
        // valido, e il click sull'uno finirebbe anche sull'altro.
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Apri ${item.nome_file ?? "foto"} ingrandita`}
          className="block h-full w-full cursor-zoom-in"
        >
          <AuthedImage src={item.file_url} alt={item.nome_file ?? ""} className="h-full w-full object-cover" />
        </button>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-ink/40">
          <FileVideo className="h-6 w-6" aria-hidden="true" />
          <span className="px-1 text-center text-[10px] leading-tight">{item.nome_file}</span>
        </div>
      )}

      {showCategoryBadge && item.categoria && (
        <span className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1 py-0.5 text-[10px] text-white">
          {item.categoria}
        </span>
      )}

      {canEdit && (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Elimina allegato"
          className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100"
        >
          <Trash2 className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// GrigliaMiniature — la griglia di miniature piu' la lightbox che ci si apre
// sopra. Condivisa da MediaUploader e da CategoryRow, cosi' il comportamento
// "clicco una foto e la vedo grande" e' scritto una volta sola e vale ovunque
// ci siano allegati (Sopralluoghi, Interventi, Installazioni, Calendario,
// Attivazioni, Clienti), non solo dove e' stato chiesto.
//
// Le frecce scorrono le foto di QUESTA griglia, cioe' esattamente quelle che
// restano dietro l'overlay: nei Sopralluoghi significa dentro la tipologia
// aperta (Locale cassaforte, Ingressi/uscite...), non attraverso tutte le foto
// del sopralluogo. E' l'unico raggruppamento che l'utente ha davanti nel
// momento in cui clicca, quindi l'unico rispetto a cui "avanti" ha un senso
// prevedibile.
//
// I video restano fuori: non sono cliccabili e non entrano nello scorrimento.
// ============================================================================
function GrigliaMiniature({
  items,
  className,
  showCategoryBadge = false,
  canEdit,
  deletingId,
  onDelete,
}: {
  items: MediaAllegato[];
  className: string;
  showCategoryBadge?: boolean;
  canEdit: boolean;
  deletingId: string | null;
  onDelete: (item: MediaAllegato) => void;
}) {
  const [indiceAperto, setIndiceAperto] = useState<number | null>(null);
  const foto = items.filter((m) => m.tipo === "foto");

  return (
    <>
      <div className={className}>
        {items.map((item) => (
          <MediaThumb
            key={item.id}
            item={item}
            isDeleting={deletingId === item.id}
            canEdit={canEdit}
            onDelete={() => onDelete(item)}
            showCategoryBadge={showCategoryBadge}
            onOpen={
              item.tipo === "foto"
                ? () => setIndiceAperto(foto.findIndex((f) => f.id === item.id))
                : undefined
            }
          />
        ))}
      </div>

      {indiceAperto !== null && (
        <MediaLightbox
          foto={foto}
          indice={indiceAperto}
          onIndice={setIndiceAperto}
          onClose={() => setIndiceAperto(null)}
        />
      )}
    </>
  );
}

// ============================================================================
// MediaUploader — griglia generica con un unico bottone di upload + select
// categoria opzionale. Usata dove le foto non hanno tipologie fisse da
// mostrare separate (Installazioni, Interventi, Calendario, Attivazioni,
// Clienti). Per i Sopralluoghi, dove ogni tipologia di foto ha il suo posto
// fisso nel modulo, vedi CategorizedMediaUploader più sotto.
// ============================================================================

interface MediaUploaderProps {
  entityType: MediaEntityType;
  entityId: string | null;
  categories?: { value: string; label: string }[];
  canEdit?: boolean;
}

export function MediaUploader({ entityType, entityId, categories, canEdit = true }: MediaUploaderProps) {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [media, setMedia] = useState<MediaAllegato[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(categories?.[0]?.value ?? "");

  async function load() {
    if (!entityId) return;
    setIsLoading(true);
    try {
      const { media: list } = await mediaApi.listMedia(entityType, entityId);
      setMedia(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare gli allegati."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !entityId) return;

    setIsUploading(true);
    try {
      await mediaApi.uploadMedia(entityType, entityId, file, selectedCategory || undefined);
      showToast("File caricato.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Caricamento non riuscito."), "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(item: MediaAllegato) {
    const confirmed = window.confirm("Eliminare questo file?");
    if (!confirmed) return;

    setDeletingId(item.id);
    try {
      await mediaApi.deleteMedia(item.id);
      showToast("File eliminato.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il file."), "error");
    } finally {
      setDeletingId(null);
    }
  }

  if (!entityId) {
    return <p className="text-xs text-ink/40">Salva prima il record per poter allegare foto e video.</p>;
  }

  return (
    <div>
      {canEdit && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {categories && categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-9 rounded-lg border border-line bg-white px-2 text-xs text-ink"
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_FOTO_VIDEO}
            onChange={handleFileSelected}
            className="hidden"
            id={`media-input-${entityType}-${entityId}`}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            Carica foto/video
          </Button>
          <span className="text-xs text-ink/40">Max 100MB per file.</span>
        </div>
      )}

      {isLoading && <p className="text-xs text-ink/40">Caricamento…</p>}

      {!isLoading && media.length === 0 && <p className="text-xs text-ink/40">Nessun allegato.</p>}

      {!isLoading && media.length > 0 && (
        <GrigliaMiniature
          items={media}
          className="grid grid-cols-3 gap-2 sm:grid-cols-4"
          showCategoryBadge
          canEdit={canEdit}
          deletingId={deletingId}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

// ============================================================================
// CategorizedMediaUploader — una riga per ciascuna tipologia richiesta, con
// il proprio bottone di scatto/carica e la propria mini-galleria. Pensato
// per i Sopralluoghi, dove il modulo reale elenca tipologie di foto precise
// (locale cassaforte, ingressi/uscite, ecc.) invece di un cestino generico.
// ============================================================================

export interface MediaCategoryDef {
  value: string;
  label: string;
  /** default `ACCEPT_FOTO` — usare `ACCEPT_FOTO_VIDEO` per categorie che ammettono anche video (es. percorso vigilanza). */
  accept?: string;
}

interface CategorizedMediaUploaderProps {
  entityType: MediaEntityType;
  entityId: string | null;
  categories: MediaCategoryDef[];
  canEdit?: boolean;
}

export function CategorizedMediaUploader({
  entityType,
  entityId,
  categories,
  canEdit = true,
}: CategorizedMediaUploaderProps) {
  const { showToast } = useToast();
  const [media, setMedia] = useState<MediaAllegato[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingCategoria, setUploadingCategoria] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function load() {
    if (!entityId) return;
    setIsLoading(true);
    try {
      const { media: list } = await mediaApi.listMedia(entityType, entityId);
      setMedia(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare gli allegati."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  async function handleUpload(categoria: string, file: File) {
    if (!entityId) return;
    setUploadingCategoria(categoria);
    try {
      await mediaApi.uploadMedia(entityType, entityId, file, categoria);
      showToast("File caricato.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Caricamento non riuscito."), "error");
    } finally {
      setUploadingCategoria(null);
    }
  }

  async function handleDelete(item: MediaAllegato) {
    const confirmed = window.confirm("Eliminare questo file?");
    if (!confirmed) return;

    setDeletingId(item.id);
    try {
      await mediaApi.deleteMedia(item.id);
      showToast("File eliminato.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il file."), "error");
    } finally {
      setDeletingId(null);
    }
  }

  if (!entityId) {
    return <p className="text-xs text-ink/40">Salva prima il record per poter allegare foto e video.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {categories.map((cat) => (
        <CategoryRow
          key={cat.value}
          category={cat}
          items={media.filter((m) => m.categoria === cat.value)}
          isLoading={isLoading}
          isUploading={uploadingCategoria === cat.value}
          deletingId={deletingId}
          canEdit={canEdit}
          onUpload={(file) => handleUpload(cat.value, file)}
          onDelete={handleDelete}
        />
      ))}
    </div>
  );
}

function CategoryRow({
  category,
  items,
  isLoading,
  isUploading,
  deletingId,
  canEdit,
  onUpload,
  onDelete,
}: {
  category: MediaCategoryDef;
  items: MediaAllegato[];
  isLoading: boolean;
  isUploading: boolean;
  deletingId: string | null;
  canEdit: boolean;
  onUpload: (file: File) => void;
  onDelete: (item: MediaAllegato) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = category.accept ?? ACCEPT_FOTO;
  // "capture" forza l'apertura diretta della fotocamera su mobile invece del
  // selettore file generico — ha senso solo quando la categoria accetta SOLO
  // immagini: con accept misto image+video il comportamento tra browser è
  // incoerente, meglio lasciare la scelta normale (fotocamera o galleria)
  // che il sistema operativo offre comunque.
  const isPhotoOnly = accept === ACCEPT_FOTO;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = "";
  }

  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">{category.label}</p>

        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              capture={isPhotoOnly ? "environment" : undefined}
              onChange={handleChange}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={isUploading}
              onClick={() => inputRef.current?.click()}
            >
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              {isPhotoOnly ? "Scatta / carica foto" : "Carica foto o video"}
            </Button>
          </>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-ink/40">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-ink/40">Nessun file caricato per questa tipologia.</p>
      ) : (
        <GrigliaMiniature
          items={items}
          className="grid grid-cols-4 gap-2 sm:grid-cols-6"
          canEdit={canEdit}
          deletingId={deletingId}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}
